"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSDEValues = exports.createOrSetResult = exports.setResult = exports.getResult = exports.hasResult = exports.findResult = exports.buildPopulationGroupRelevanceMap = exports.buildPopulationRelevanceMap = exports.buildPopulationRelevanceForAllEpisodes = exports.doesResultPass = exports.findResultForStatementClause = exports.setFinalResults = exports.prettyFHIRObject = exports.prettyConcept = exports.prettyCode = exports.prettyResult = exports.buildStatementAndClauseResults = exports.getStatementResult = exports.markStatementRelevant = exports.buildStatementRelevanceMap = void 0;
const ClauseResultsHelpers = __importStar(require("../helpers/ClauseResultsHelpers"));
const MeasureBundleHelpers = __importStar(require("../helpers/MeasureBundleHelpers"));
const ELMDependencyHelper = __importStar(require("../helpers/elm/ELMDependencyHelpers"));
const cql_execution_1 = require("cql-execution");
const moment_1 = __importDefault(require("moment"));
const Enums_1 = require("../types/Enums");
const Calculator_1 = require("../types/Calculator");
const CustomErrors_1 = require("../types/errors/CustomErrors");
// code system, search parameters, and code paths for pulling pretty information from resources
const systemMap_1 = require("fhir-spec-tools/build/data/systemMap");
const SearchParameters_1 = require("../compartment-definition/SearchParameters");
const codePaths_1 = require("fhir-spec-tools/build/data/codePaths");
const clinicalDateParams = (_a = SearchParameters_1.SearchParameters.entry.find(e => e.fullUrl === 'http://hl7.org/fhir/SearchParameter/clinical-date')) === null || _a === void 0 ? void 0 : _a.resource;
/**
 * Contains helpers that generate useful data for coverage and highlighting.
 */
/**
 * Builds the initial list of StatementResult objects. This initializes the objects with ith statement relevance info.
 * This list gets added to the DetailedPopulationGroupResult for the given population group.
 *
 * The statement relevance indicates which define statements were actually relevant to a population inclusion
 * consideration. This makes use of the 'population relevance' info. The values for statement relevance differ from the
 * `population relevance` because we also need to track statements that are not used for any population calculation.
 * Therefore the values are a string/enum that is one of the following: 'NA', 'TRUE', 'FALSE'. Here is what they mean:
 *
 * 'NA' - Not applicable. This statement is not relevant to any population calculation in this population_set. Common
 *   for unused library statements or statements only used for other population sets.
 *
 * 'FALSE' - This statement is not relevant to any of this patient's population inclusion calculations.
 *
 * 'TRUE' - This statement is relevant for one or more of the population inclusion calculations.
 *
 * This function relies heavily on the statement dependency map built in ELMDependencyHelper to recursively determine
 * which statements are used in the relevant population statements. It also uses the 'population_relevance' map to
 * determine the relevance of the population defining statement and its dependent statements.
 *
 * @public
 * @param {fhir4.Measure} measure - The measure.
 * @param {PopulationResult[]} populationRelevanceSet - The population relevance results, used at the starting point.
 * @param {string} mainLibraryId - The identifier of the main library.
 * @param {ELM[]} elmLibraries - All ELM libraries for the measure.
 * @param {fhir4.MeasureGroup} populationGroup - The population group being calculated.
 * @param {boolean} calculateSDEs - Whether or not to treat SDEs as calculated/relevant or not.
 * @param {boolean} calculateRAVs - Whether or not to treat RAVs as calculated/relevant or not.
 * @returns {StatementResult[]} The StatementResults list for each statement with its relevance status populated.
 */
function buildStatementRelevanceMap(measure, populationRelevanceSet, mainLibraryId, elmLibraries, populationGroup, calculateSDEs, calculateRAVs) {
    var _a, _b;
    // build statement results defaulting to not applicable (NA)
    const statementResults = [];
    elmLibraries.forEach(elmLibrary => {
        var _a;
        (_a = elmLibrary.library.statements) === null || _a === void 0 ? void 0 : _a.def.forEach(statement => {
            const statementResult = {
                libraryName: elmLibrary.library.identifier.id,
                statementName: statement.name,
                localId: statement.localId,
                final: Enums_1.FinalResult.NA,
                relevance: Enums_1.Relevance.NA
            };
            statementResults.push(statementResult);
        });
    });
    // build statement dependency maps to use for marking relevant statements
    // TODO: Move this out to only happen once per measure to improve performance.
    const statementDependencies = ELMDependencyHelper.buildStatementDependencyMaps(elmLibraries);
    // Calculate SDEs and/or RAVs if specified
    if ((calculateSDEs || calculateRAVs) && measure.supplementalData) {
        measure.supplementalData.forEach(sde => {
            var _a, _b, _c, _d, _e, _f, _g;
            const matchUsage = (((_c = (_b = (_a = sde.usage) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.coding) === null || _c === void 0 ? void 0 : _c[0].code) === 'supplemental-data' && calculateSDEs) ||
                (((_f = (_e = (_d = sde.usage) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.coding) === null || _f === void 0 ? void 0 : _f[0].code) === 'risk-adjustment-factor' && calculateRAVs);
            if (((_g = sde.criteria) === null || _g === void 0 ? void 0 : _g.expression) && matchUsage) {
                markStatementRelevant(elmLibraries, statementDependencies, statementResults, mainLibraryId, sde.criteria.expression, true);
            }
        });
    }
    // if we have stratifications, mark all strata statements relevant
    (_a = populationGroup.stratifier) === null || _a === void 0 ? void 0 : _a.forEach(strata => {
        var _a;
        if ((_a = strata.criteria) === null || _a === void 0 ? void 0 : _a.expression) {
            markStatementRelevant(elmLibraries, statementDependencies, statementResults, mainLibraryId, strata.criteria.expression, true);
        }
    });
    // Iterate over all populations in this group and mark their statements relevant.
    (_b = populationGroup.population) === null || _b === void 0 ? void 0 : _b.forEach(population => {
        const popType = MeasureBundleHelpers.codeableConceptToPopulationType(population.code);
        if (popType) {
            // If the population is values, that means we need to mark relevance for the OBSERVs
            const relevance = getResult(popType, populationRelevanceSet);
            const relevantStatement = population.criteria.expression;
            if (relevantStatement) {
                markStatementRelevant(elmLibraries, statementDependencies, statementResults, mainLibraryId, relevantStatement, relevance);
            }
        }
    });
    return statementResults;
}
exports.buildStatementRelevanceMap = buildStatementRelevanceMap;
/**
 * Recursive helper function for the buildStatementRelevanceMap function. This marks a statement as relevant (or not
 * relevant but applicable) in the StatementResults list. It recurses and marks dependent statements also relevant
 * unless they have already been marked as 'TRUE' for their relevance status. This function will never be called on
 * statements that are 'NA'.
 *
 * @private
 * @param {ELM[]} elmLibraries
 * @param {LibraryDependencyInfo[]} statementDependencies - dependency map from the measure object. The thing we recurse
 *   over to find dependent statements even though it is flat, it represents a tree.
 * @param {StatementResult[]} statementResults - The list of StatementResults to mark relevance in.
 * @param {string} libraryName - The library name of the statement we are marking.
 * @param {string} statementName - The name of the statement we are marking.
 * @param {boolean} relevant - true if the statement should be marked 'TRUE', false if it should be marked 'FALSE'.
 */
function markStatementRelevant(elmLibraries, statementDependencies, statementResults, libraryName, statementName, relevant) {
    // only mark the statement if it is currently 'NA' or 'FALSE'. Otherwise it already has been marked 'TRUE'
    const statementResult = getStatementResult(libraryName, statementName, statementResults);
    if ((statementResult === null || statementResult === void 0 ? void 0 : statementResult.relevance) == Enums_1.Relevance.NA || (statementResult === null || statementResult === void 0 ? void 0 : statementResult.relevance) == Enums_1.Relevance.FALSE) {
        statementResult.relevance = relevant ? Enums_1.Relevance.TRUE : Enums_1.Relevance.FALSE;
        // grab the dependency info for this statement
        const libraryInfo = statementDependencies.find(lib => lib.libraryId === libraryName);
        const statementInfo = libraryInfo === null || libraryInfo === void 0 ? void 0 : libraryInfo.statementDependencies.find(stat => stat.statementName === statementName);
        if (statementInfo) {
            statementInfo.statementReferences.forEach(dependentStatement => {
                markStatementRelevant(elmLibraries, statementDependencies, statementResults, dependentStatement.libraryId, dependentStatement.statementName, relevant);
            });
        }
    }
}
exports.markStatementRelevant = markStatementRelevant;
/**
 * Helper function to find the StatementResult object for a given statement.
 *
 * @param {string} libraryName - Name of libary.
 * @param {string} statementName - Name of statement.
 * @param {StatementResult[]} statementResults - List of statement results to find the result in.
 * @returns {StatementResult|undefined} The statement results if found.
 */
function getStatementResult(libraryName, statementName, statementResults) {
    return statementResults.find(result => {
        return result.libraryName == libraryName && result.statementName == statementName;
    });
}
exports.getStatementResult = getStatementResult;
/**
 * Fills out the results attributes for the statements and builds clause results.
 *
 * The StatementResult `final` field indicates the result for the statement taking into account the statement
 * relevance in determining the result. The StatementResult also has the properties, 'raw', 'final' and 'pretty'. 'raw'
 * is the raw result from the execution engine for that statement. 'final' is the final result that takes into account
 * the relevance in this calculation. 'pretty' is a human readable description of the result that is only generated if
 * doPretty is true. The value of 'final' will be one of the following strings/enums: 'NA', 'UNHIT', 'TRUE', 'FALSE'.
 *
 * Here's what they mean:
 *
 * 'NA' - Not applicable. This statement is not relevant to any population calculation in this population_set. Common
 *   for unused library statements or statements only used for other population sets.
 *   !!!IMPORTANT NOTE!!! All define function statements are marked 'NA' since we don't have a strategy for
 *        highlighting or coverage when it comes to functions.
 *
 * 'UNHIT' - This statement wasn't hit. This is most likely because the statement was not relevant to population
 *     calculation for this patient. i.e. 'FALSE' in the the `statement_relevance` map.
 *
 * 'TRUE' - This statement is relevant and has a truthy result.
 *
 * 'FALSE' - This statement is relevant and has a falsey result.
 *
 * The ClauseResult list is similar to the StatementResult list but it indicates the results for each clause.
 * The same 'raw' and 'final' fields are set, but for the the clause itself.
 *
 * This function relies very heavily on the StatementResult `relevance` field to determine the final results.
 *
 * @public
 * @param {fhir4.Measure} measure - The measure.
 * @param {ELM[]} elmLibraries - List of all ELM Library JSONs.
 * @param {any} rawClauseResults - The raw clause results from the calculation engine.
 * @param {StatementResult[]} statementResults - The `statement_relevance` map. Used to determine if they were hit or not.
 * @param {boolean} doPretty - If true, also generate pretty versions of result.
 * @returns {object} Object with the statement_results and clause_results structures, keyed as such.
 */
function buildStatementAndClauseResults(measure, elmLibraries, rawClauseResults, statementResults, doPretty, includeClauseResults) {
    if (doPretty == null) {
        doPretty = false;
    }
    const clauseResults = [];
    // Iterate over statement results that already had relevance information populated and fill out
    // raw and final result information.
    statementResults.forEach(statementResult => {
        const elmLibrary = elmLibraries.find(library => library.library.identifier.id == statementResult.libraryName);
        // If the elm for the library is not found skip. Should never happen.
        if (!elmLibrary) {
            return;
        }
        const rawStatementResult = findResultForStatementClause(elmLibrary, statementResult.statementName, rawClauseResults);
        statementResult.raw = rawStatementResult;
        const isFunction = ClauseResultsHelpers.isStatementFunction(elmLibrary, statementResult.statementName);
        // set isFunction property so we can later filter out functions during clause coverage calculation
        statementResult.isFunction = isFunction;
        //TODO: determine how SDEs should be handled
        //const isSDE = ClauseResultsHelpers.isSupplementalDataElementStatement(measure.supplementalData, statement_name);
        if ( /*(!measure.calculate_sdes && isSDE) || */statementResult.relevance == Enums_1.Relevance.NA) {
            statementResult.final = Enums_1.FinalResult.NA;
            if (doPretty) {
                statementResult.pretty = 'NA';
            }
        }
        else if (statementResult.relevance == Enums_1.Relevance.FALSE || rawClauseResults[statementResult.libraryName] == null) {
            statementResult.final = Enums_1.FinalResult.UNHIT;
            // even if the statement wasn't hit, we want the pretty result to just
            // be FUNCTION for functions
            if (doPretty) {
                if (isFunction) {
                    statementResult.pretty = 'FUNCTION';
                }
                else {
                    statementResult.pretty = 'UNHIT';
                }
            }
        }
        else if (doesResultPass(rawStatementResult)) {
            statementResult.final = Enums_1.FinalResult.TRUE;
            if (doPretty) {
                statementResult.pretty = prettyResult(rawStatementResult);
            }
        }
        else {
            statementResult.final = Enums_1.FinalResult.FALSE;
            if (rawStatementResult instanceof Array && rawStatementResult.length === 0) {
                // Special case, handle empty array.
                if (doPretty) {
                    statementResult.pretty = 'FALSE ([])';
                }
            }
            else if (isFunction) {
                if (doPretty) {
                    statementResult.pretty = 'FUNCTION';
                }
            }
            else if (doPretty) {
                statementResult.pretty = `FALSE (${rawStatementResult})`;
            }
        }
        if (includeClauseResults) {
            // create clause results for all localIds in this statement
            const localIds = ClauseResultsHelpers.findLocalIdsInStatementByName(elmLibrary, statementResult.statementName);
            for (const localId in localIds) {
                const clause = localIds[localId];
                const rawClauseResult = rawClauseResults[statementResult.libraryName] != null
                    ? rawClauseResults[statementResult.libraryName][clause.sourceLocalId != null ? clause.sourceLocalId : localId]
                    : undefined;
                const clauseResult = {
                    // if this clause is an alias or a usage of alias it will get the raw result from the sourceLocalId.
                    raw: rawClauseResult,
                    statementName: statementResult.statementName,
                    libraryName: statementResult.libraryName,
                    localId,
                    final: Enums_1.FinalResult.NA
                };
                clauseResult.final = setFinalResults({
                    rawClauseResults,
                    statementRelevance: statementResult.relevance,
                    libraryName: statementResult.libraryName,
                    clause,
                    rawResult: clauseResult.raw
                });
                clauseResults.push(clauseResult);
            }
        }
    });
    return clauseResults;
}
exports.buildStatementAndClauseResults = buildStatementAndClauseResults;
/**
 * Generates a pretty human readable representation of a result.
 *
 * @param {(Array|object|boolean|???)} result - The result from the calculation engine.
 * @param {boolean} includeType - true if certain types should be labeled in the pretty output, like 'CODE'
 * @param {number|undefined} indentLevel - For nested objects, the indentLevel indicates how far to indent.
 *                                Note that 1 is the base because Array(1).join ' ' returns ''.
 * @param {number|undefined} keyIndent - Indent count used for key indentation.
 * @returns {String} a pretty version of the given result
 */
function prettyResult(result, includeType = true, indentLevel, keyIndent) {
    let prettyResultReturn;
    if (indentLevel == null) {
        indentLevel = 1;
    }
    if (keyIndent == null) {
        keyIndent = 1;
    }
    const keyIndentation = Array(keyIndent).join(' ');
    const currentIndentation = Array(indentLevel).join(' ');
    if (result instanceof cql_execution_1.DateTime) {
        return moment_1.default.utc(result.toString()).format('MM/DD/YYYY h:mm:ss A');
    }
    else if (result instanceof cql_execution_1.Interval) {
        const typeStr = includeType ? 'INTERVAL: ' : '';
        return `${typeStr}${prettyResult(result.low)} - ${prettyResult(result.high)}`;
    }
    else if (result instanceof cql_execution_1.Code) {
        return prettyCode(result.code, result.system, result.display, includeType);
    }
    else if (result instanceof cql_execution_1.Concept) {
        return prettyConcept(result.display, result.codes, includeType, indentLevel, keyIndent);
    }
    else if (result instanceof cql_execution_1.Quantity) {
        let quantityResult = `QUANTITY: ${result.value}`;
        if (result.unit) {
            quantityResult += ` ${result.unit}`;
        }
        return quantityResult;
    }
    else if (typeof result === 'string') {
        return `"${result}"`;
    }
    else if (Array.isArray(result)) {
        prettyResultReturn = result.map((value) => prettyResult(value, includeType, indentLevel, keyIndent));
        return `[${prettyResultReturn.join(`,\n${currentIndentation}${keyIndentation}`)}]`;
    }
    else if (result != null && typeof result === 'object') {
        // if the object has it's own custom toString method, use that instead
        if (typeof result.toString === 'function' && result.toString !== Object.prototype.toString) {
            return result.toString();
        }
        if (typeof result.getTypeInfo === 'function') {
            // FHIRObject approach
            return prettyFHIRObject(result, includeType, indentLevel, keyIndent);
        }
        else {
            // Tuple approach
            prettyResultReturn = '{\n';
            const baseIndentation = Array(3).join(' ');
            const sortedKeys = Object.keys(result).sort();
            for (const key of sortedKeys) {
                // add 2 spaces per indent
                const value = result[key];
                const nextIndentLevel = indentLevel + 2;
                // key length + ': '
                keyIndent = key.length + 3;
                prettyResultReturn = prettyResultReturn.concat(`${baseIndentation}${currentIndentation}${key}: ${prettyResult(value, false, nextIndentLevel, keyIndent)}`);
                // append commas if it isn't the last key
                if (key === sortedKeys[sortedKeys.length - 1]) {
                    prettyResultReturn += '\n';
                }
                else {
                    prettyResultReturn += ',\n';
                }
            }
            prettyResultReturn += `${currentIndentation}}`;
            return prettyResultReturn;
        }
    }
    if (result) {
        return JSON.stringify(result, null, 2);
    }
    return 'null';
}
exports.prettyResult = prettyResult;
/**
 * Uses the pieces of a code object to show code, system, and display in a pretty way
 * for whichever properties are available.
 */
function prettyCode(code, system, display, includeType) {
    const typeStr = includeType ? 'CODE: ' : '';
    const displayStr = display ? `, ${display}` : '';
    let systemStr = 'UNDEFINED_SYSTEM';
    if (system) {
        if (system in systemMap_1.systemMap) {
            systemStr = systemMap_1.systemMap[system];
        }
        else {
            systemStr = system;
        }
    }
    return `${typeStr}${systemStr} ${code}${displayStr}`;
}
exports.prettyCode = prettyCode;
/**
 * Uses the pieces of a codeable concept object to show concept display text (if available)
 * and the contained codings that belong to the concept in a pretty way
 */
function prettyConcept(conceptTxt, conceptCodings, includeType, indentLevel, keyIndent) {
    let prettyConcept = '';
    let keyDiff = 0;
    if (includeType) {
        prettyConcept = 'CONCEPT: ';
        keyDiff = 2; //offset between concept key and code key including bracket
    }
    let conceptTxtNL = '';
    if (conceptTxt || includeType) {
        // include new line (+ indentation) if there's something to help dilineate the coding list
        const keyIndentation = Array((keyIndent !== null && keyIndent !== void 0 ? keyIndent : 0) + keyDiff).join(' ');
        conceptTxtNL = `\n${keyIndentation}`;
    }
    if (conceptTxt) {
        prettyConcept = prettyConcept.concat(`${conceptTxt}`);
    }
    if (conceptCodings) {
        prettyConcept = prettyConcept.concat(`${conceptTxtNL}${prettyResult(conceptCodings, includeType, indentLevel, (keyIndent !== null && keyIndent !== void 0 ? keyIndent : 0) + (includeType ? keyDiff + 1 : keyDiff) // +1 for bracket
        )}`);
    }
    return prettyConcept;
}
exports.prettyConcept = prettyConcept;
/**
 * Function to help prettyResult handle FHIRObject(s) specifically, handling similar types, but
 * in a FHIRObject structure rather than a CQL object structure. Uses similar inputs put expects
 * a FHIRObject result to be passed in.
 */
function prettyFHIRObject(result, includeType = true, indentLevel, keyIndent) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const resourceType = result.getTypeInfo()._name;
    const keyIndentation = Array(keyIndent).join(' ');
    const typeStr = includeType ? `${resourceType.toUpperCase()}: ` : '';
    if (resourceType === 'Period') {
        return `${typeStr}${prettyResult(result.get('start'))} - ${prettyResult(result.get('end'))}`;
    }
    else if (resourceType === 'dateTime') {
        return `${moment_1.default.utc(result.get('value').toString()).format('MM/DD/YYYY h:mm:ss A')}`;
    }
    else if (resourceType === 'date') {
        return `${moment_1.default.utc(result.get('value').toString()).format('MM/DD/YYYY')}`;
    }
    else if (resourceType === 'Coding') {
        return prettyCode((_a = result.get('code')) === null || _a === void 0 ? void 0 : _a.value, (_b = result.get('system')) === null || _b === void 0 ? void 0 : _b.value, (_c = result.get('display')) === null || _c === void 0 ? void 0 : _c.value, includeType);
    }
    else if (resourceType === 'CodeableConcept') {
        return prettyConcept((_d = result.get('text')) === null || _d === void 0 ? void 0 : _d.value, result.get('coding'), includeType, indentLevel, keyIndent);
    }
    else if (resourceType === 'Duration') {
        return `${typeStr}${(_e = result.get('value')) === null || _e === void 0 ? void 0 : _e.value} ${(_f = result.get('unit')) === null || _f === void 0 ? void 0 : _f.value}`;
    }
    else if (resourceType === 'Ratio') {
        const numer = (_h = (_g = result.get('numerator')) === null || _g === void 0 ? void 0 : _g.value) === null || _h === void 0 ? void 0 : _h.value;
        const numerUnit = (_k = (_j = result.get('numerator')) === null || _j === void 0 ? void 0 : _j.unit) === null || _k === void 0 ? void 0 : _k.value;
        const denom = (_m = (_l = result.get('denominator')) === null || _l === void 0 ? void 0 : _l.value) === null || _m === void 0 ? void 0 : _m.value;
        const denomUnit = (_p = (_o = result.get('denominator')) === null || _o === void 0 ? void 0 : _o.unit) === null || _p === void 0 ? void 0 : _p.value;
        return `${typeStr}(${numer}${numerUnit ? ` ${numerUnit}` : ''})/(${denom}${denomUnit ? ` ${denomUnit}` : ''})`;
    }
    if (resourceType in codePaths_1.parsedCodePaths) {
        // Handle resources
        let prettyResultReturn = '';
        const currentIndentation = Array(indentLevel).join(' ');
        prettyResultReturn = prettyResultReturn.concat(`${resourceType}\n`);
        prettyResultReturn = prettyResultReturn.concat(`${currentIndentation}${keyIndentation}ID: ${result.getId()}\n`);
        if (clinicalDateParams === null || clinicalDateParams === void 0 ? void 0 : clinicalDateParams.base.includes(resourceType)) {
            const expressionMatch = clinicalDateParams.expression.split('|').find(e => e.trim().startsWith(resourceType));
            const expression = (expressionMatch === null || expressionMatch === void 0 ? void 0 : expressionMatch.split('.')[1].trim()) || '';
            prettyResultReturn = prettyResultReturn.concat(`${currentIndentation}${keyIndentation}${expression.toUpperCase()}: ${prettyResult(result.getDateOrInterval(expression), false)}\n`);
        }
        const codePath = codePaths_1.parsedCodePaths[resourceType].primaryCodePath;
        prettyResultReturn = prettyResultReturn.concat(`${currentIndentation}${keyIndentation}${codePath.toUpperCase()}: ${prettyResult(result.getCode(codePath), false, indentLevel, (keyIndent !== null && keyIndent !== void 0 ? keyIndent : 0) + codePath.length + 2 // key length + 2 for colon, space
        )}`);
        return prettyResultReturn;
    }
    // Handle values
    if (result.getTypeInfo().findElement('value', true)) {
        const value = result.get('value');
        if (typeof value === 'string' || typeof value === 'boolean' || typeof value === 'number') {
            // Try simple value
            return `${typeStr}${value}`;
        }
        else if (typeof (value === null || value === void 0 ? void 0 : value.value) === 'string' ||
            typeof (value === null || value === void 0 ? void 0 : value.value) === 'boolean' ||
            typeof (value === null || value === void 0 ? void 0 : value.value) === 'number') {
            // Try nested value
            return `${typeStr}${value === null || value === void 0 ? void 0 : value.value}`;
        }
    }
    // Unknown
    return `(atypical type) ${typeStr}\n${JSON.stringify(result, null, 2)}`;
}
exports.prettyFHIRObject = prettyFHIRObject;
/**
 * Determines the final result (for coloring and coverage) for a clause. The result fills the 'final' property for the
 * clause result. Look at the comments for buildStatementAndClauseResults to get a description of what each of the
 * string results of this function are.
 * @private
 * @param {object} rawClauseResults - The raw clause results from the calculation engine.
 * @param {object} statementRelevance - The statement relevance map.
 * @param {object} statement_name - The name of the statement the clause is in
 * @param {object} library_name - The name of the libarary the clause is in
 * @param {object} localId - The localId of the current clause
 * @param {object} clause - The clause we are getting the final result of
 * @param {Array|Object|Interval|??} rawResult - The raw result from the calculation engine.
 * @returns {string} The final result for the clause.
 */
function setFinalResults(params) {
    let finalResult = Enums_1.FinalResult.FALSE;
    if (params.clause.isUnsupported != null) {
        finalResult = Enums_1.FinalResult.NA;
    }
    else if (params.statementRelevance == Enums_1.Relevance.NA) {
        finalResult = Enums_1.FinalResult.NA;
    }
    else if (params.statementRelevance == Enums_1.Relevance.FALSE || params.rawClauseResults[params.libraryName] == null) {
        finalResult = Enums_1.FinalResult.UNHIT;
    }
    else if (params.clause.isFalsyLiteral &&
        params.rawClauseResults[params.libraryName].hasOwnProperty(params.clause.localId)) {
        // If this clause is a Null or Literal False we need to look for the existence of a result for the localId in the
        // rawClauseResults instead. If the key for the localId exists then it was executed and we will want to treat the
        // `final` result as `TRUE` instead of `FALSE`. If the key is totally absent then it was not executed therefore
        // `final` result should stay `FALSE`.
        finalResult = Enums_1.FinalResult.TRUE;
    }
    else if (doesResultPass(params.rawResult)) {
        finalResult = Enums_1.FinalResult.TRUE;
    }
    return finalResult;
}
exports.setFinalResults = setFinalResults;
/**
 * Finds the clause localId for a statement and gets the raw result for it from the raw clause results.
 * @private
 * @param {ELM} library - The library
 * @param {string} statement - The statement
 * @param {cql.LocalIdResults} rawClauseResults - The raw clause results from the engine.
 * @returns {(Array|object|Interval|??)} The raw result from the calculation engine for the given statement.
 */
function findResultForStatementClause(elm, statementName, rawClauseResults) {
    const elmStatement = elm.library.statements.def.find(def => def.name === statementName);
    const libraryName = elm.library.identifier.id;
    const localId = elmStatement === null || elmStatement === void 0 ? void 0 : elmStatement.localId;
    if (localId != undefined) {
        return rawClauseResults[libraryName] != null ? rawClauseResults[libraryName][localId] : undefined;
    }
    else {
        return undefined;
    }
}
exports.findResultForStatementClause = findResultForStatementClause;
/**
 * Determines if a result (for a statement or clause) from the execution engine is a pass or fail.
 * @private
 * @param {(Array|object|boolean|???)} result - The result from the calculation engine.
 * @returns {boolean} true or false
 */
function doesResultPass(result) {
    if (result === true) {
        // Specifically a boolean true
        return true;
    }
    else if (result === false) {
        // Specifically a boolean false
        return false;
    }
    else if (Array.isArray(result)) {
        // Check if result is an array
        if (result.length === 0) {
            // Result is true if the array is not empty
            return false;
        }
        else if (result.length === 1 && result[0] === null) {
            // But if the array has one element that is null. Then we should make it red.
            return false;
        }
        return true;
    }
    else if (result instanceof cql_execution_1.Interval) {
        // make it green if and Interval is returned
        return true;
        // Return false if an empty CQL Code is the result
    }
    else if (result instanceof cql_execution_1.Code && result.code == null) {
        return false;
    }
    else if (result === null || result === undefined) {
        // Specifically no result
        return false;
    }
    return true;
}
exports.doesResultPass = doesResultPass;
/**
 * Iterate over episode results, call _buildPopulationRelevanceMap for each result then OR population relevances
 * together so that a patient level population relevance set can be made.
 *
 * @private
 * @param {EpisodeResults[]} episodeResultsSet - Results for each episode
 * @param {fhir4.MeasureGroup} populationGroup - The population group from the measure
 * @param {string} measureScoringCode - The scoring code for the measure (used if scoring code not provided at the group level)
 * @returns {PopulationResult[]} List denoting if population calculation was considered/relevant in any episode
 */
function buildPopulationRelevanceForAllEpisodes(episodeResultsSet, populationGroup, measureScoringCode) {
    var _a;
    const masterRelevanceResults = ((_a = populationGroup.population) === null || _a === void 0 ? void 0 : _a.map(population => {
        return {
            populationType: MeasureBundleHelpers.codeableConceptToPopulationType(population.code),
            criteriaExpression: population.criteria.expression,
            result: false
        };
    })) || []; // Should not end up becoming an empty list.
    episodeResultsSet.forEach(episodeResults => {
        const episodeRelevance = buildPopulationRelevanceMap(episodeResults.populationResults, populationGroup, measureScoringCode);
        masterRelevanceResults.forEach(masterPopResults => {
            // find relevance in episode and if true, make master relevance true, only if not already true
            if (masterPopResults.result === false) {
                const matchingEpisodeResult = findResult(masterPopResults.populationType, episodeRelevance, masterPopResults.criteriaExpression);
                if (matchingEpisodeResult) {
                    if (matchingEpisodeResult.populationId) {
                        masterPopResults.populationId = matchingEpisodeResult.populationId;
                    }
                    if (matchingEpisodeResult.criteriaReferenceId) {
                        masterPopResults.criteriaReferenceId = matchingEpisodeResult.criteriaReferenceId;
                    }
                    if (matchingEpisodeResult.result === true) {
                        masterPopResults.result = true;
                    }
                }
            }
        });
    });
    return masterRelevanceResults;
}
exports.buildPopulationRelevanceForAllEpisodes = buildPopulationRelevanceForAllEpisodes;
/**
 * Builds the relevance results for each population. This creates the `populationRelevance` list that is put on
 * DetailedPopulationGroupResult
 *
 * For each population in the group it is set to true if the population was relevant/considered, false if
 * NOT relevant/considered. This is used later to determine which define statements are relevant in the calculation.
 *
 * For example: If they aren't in the IPP then they are not going to be considered for any other population and all other
 * populations will be marked NOT relevant.
 *
 * This function is extremely verbose because this is an important and confusing calculation to make. The verbosity
 * was kept to make it more maintainable.
 *
 * @param {PopulationResult[]} results - The population results list for the population results.
 * @param {fhir4.MeasureGroup} group - The full group of the Measure, which is useful for resolving references between different populations
 * @param {string} measureScoringCode - The scoring code for measure (used if scoring code not provided at the group level)
 * @returns {PopulationResult[]} The population relevance set.
 */
function buildPopulationRelevanceMap(results, group, measureScoringCode) {
    // Create initial results starting with all true to create the basis for relevance.
    const relevantResults = results.map(result => {
        return Object.assign(Object.assign(Object.assign({}, (result.populationId ? { populationId: result.populationId } : {})), (result.criteriaReferenceId ? { criteriaReferenceId: result.criteriaReferenceId } : {})), { populationType: result.populationType, criteriaExpression: result.criteriaExpression, result: true });
    });
    // If the group has multiple IPPs, they are treated independently
    // This means that a given IPP affects the numerator _only if_ that numerator uses a criteriaReference to reference that IPP
    // Same logic applies with denominator
    if (group && MeasureBundleHelpers.hasMultipleIPPs(group)) {
        const numerRelevantIPP = MeasureBundleHelpers.getRelevantIPPFromPopulation(group, Enums_1.PopulationType.NUMER);
        if (numerRelevantIPP) {
            if (getResult(Enums_1.PopulationType.IPP, results, numerRelevantIPP.criteria.expression) === false) {
                if (hasResult(Enums_1.PopulationType.NUMER, relevantResults)) {
                    setResult(Enums_1.PopulationType.NUMER, false, relevantResults);
                    setResult(Enums_1.PopulationType.NUMEX, false, relevantResults);
                }
            }
        }
        const denomRelevantIPP = MeasureBundleHelpers.getRelevantIPPFromPopulation(group, Enums_1.PopulationType.DENOM);
        if (denomRelevantIPP) {
            if (getResult(Enums_1.PopulationType.IPP, results, denomRelevantIPP.criteria.expression) === false) {
                if (hasResult(Enums_1.PopulationType.DENOM, relevantResults)) {
                    setResult(Enums_1.PopulationType.DENOM, false, relevantResults);
                    setResult(Enums_1.PopulationType.DENEX, false, relevantResults);
                    setResult(Enums_1.PopulationType.DENEXCEP, false, relevantResults);
                }
            }
        }
    }
    else if (getResult(Enums_1.PopulationType.IPP, results) === false) {
        // If IPP is false then everything else is not calculated
        if (hasResult(Enums_1.PopulationType.NUMER, relevantResults)) {
            setResult(Enums_1.PopulationType.NUMER, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.NUMEX, relevantResults)) {
            setResult(Enums_1.PopulationType.NUMEX, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.DENOM, relevantResults)) {
            setResult(Enums_1.PopulationType.DENOM, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.DENEX, relevantResults)) {
            setResult(Enums_1.PopulationType.DENEX, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.DENEXCEP, relevantResults)) {
            setResult(Enums_1.PopulationType.DENEXCEP, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.MSRPOPL, relevantResults)) {
            setResult(Enums_1.PopulationType.MSRPOPL, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.MSRPOPLEX, relevantResults)) {
            setResult(Enums_1.PopulationType.MSRPOPLEX, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.OBSERV, relevantResults)) {
            setResult(Enums_1.PopulationType.OBSERV, false, relevantResults);
        }
    }
    // If DENOM is false then DENEX, DENEXCEP, NUMER and NUMEX are not calculated
    if (hasResult(Enums_1.PopulationType.DENOM, results) && getResult(Enums_1.PopulationType.DENOM, results) === false) {
        // Do not apply this to ratio measures (numerator and denominator are independent from each other)
        if (!MeasureBundleHelpers.isRatioMeasure(group, measureScoringCode)) {
            if (hasResult(Enums_1.PopulationType.NUMER, relevantResults)) {
                setResult(Enums_1.PopulationType.NUMER, false, relevantResults);
            }
            if (hasResult(Enums_1.PopulationType.NUMEX, relevantResults)) {
                setResult(Enums_1.PopulationType.NUMEX, false, relevantResults);
            }
        }
        if (hasResult(Enums_1.PopulationType.DENEX, relevantResults)) {
            setResult(Enums_1.PopulationType.DENEX, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.DENEXCEP, relevantResults)) {
            setResult(Enums_1.PopulationType.DENEXCEP, false, relevantResults);
        }
    }
    // If DENEX is true then NUMER, NUMEX and DENEXCEP not calculated
    if (hasResult(Enums_1.PopulationType.DENEX, results) && getResult(Enums_1.PopulationType.DENEX, results) === true) {
        // Do not apply this to ratio measures (numerator and denominator are independent from each other)
        if (!MeasureBundleHelpers.isRatioMeasure(group, measureScoringCode)) {
            if (hasResult(Enums_1.PopulationType.NUMER, relevantResults)) {
                setResult(Enums_1.PopulationType.NUMER, false, relevantResults);
            }
            if (hasResult(Enums_1.PopulationType.NUMEX, relevantResults)) {
                setResult(Enums_1.PopulationType.NUMEX, false, relevantResults);
            }
        }
        if (hasResult(Enums_1.PopulationType.DENEXCEP, relevantResults)) {
            setResult(Enums_1.PopulationType.DENEXCEP, false, relevantResults);
        }
    }
    // If NUMER is false then NUMEX is not calculated
    if (hasResult(Enums_1.PopulationType.NUMER, results) && getResult(Enums_1.PopulationType.NUMER, results) === false) {
        if (hasResult(Enums_1.PopulationType.NUMEX, relevantResults)) {
            setResult(Enums_1.PopulationType.NUMEX, false, relevantResults);
        }
    }
    // If NUMER is true then DENEXCEP is not calculated
    // Do not apply this to ratio measures (numerator and denominator are independent from each other)
    if (hasResult(Enums_1.PopulationType.NUMER, results) &&
        getResult(Enums_1.PopulationType.NUMER, results) === true &&
        !MeasureBundleHelpers.isRatioMeasure(group, measureScoringCode)) {
        if (hasResult(Enums_1.PopulationType.DENEXCEP, relevantResults)) {
            setResult(Enums_1.PopulationType.DENEXCEP, false, relevantResults);
        }
    }
    // If MSRPOPL is false then OBSERVs and MSRPOPLEX are not calculateed
    if (hasResult(Enums_1.PopulationType.MSRPOPL, results) && getResult(Enums_1.PopulationType.MSRPOPL, results) === false) {
        // values is the OBSERVs
        if (hasResult(Enums_1.PopulationType.OBSERV, relevantResults)) {
            setResult(Enums_1.PopulationType.OBSERV, false, relevantResults);
        }
        if (hasResult(Enums_1.PopulationType.MSRPOPLEX, relevantResults)) {
            setResult(Enums_1.PopulationType.MSRPOPLEX, false, relevantResults);
        }
    }
    // If MSRPOPLEX is true then OBSERVs are not calculated
    if (hasResult(Enums_1.PopulationType.MSRPOPLEX, results) && getResult(Enums_1.PopulationType.MSRPOPLEX, results) === true) {
        if (hasResult(Enums_1.PopulationType.OBSERV, relevantResults)) {
            setResult(Enums_1.PopulationType.OBSERV, false, relevantResults);
        }
    }
    return relevantResults;
}
exports.buildPopulationRelevanceMap = buildPopulationRelevanceMap;
/**
 * Wrapper function that builds population relevance either for all episodes (if the measure is episode-based) or for each
 * population (if patient-based).
 * @param results - The detailed results (used to pull off episode results for episode of care measure, or population results for
 * patient-based measure)
 * @param group - The full group of the Measure
 * @param measureScoringCode - The scoring code for measure (used if scoring code not provided at the group level)
 * @returns The population relevance set.
 */
function buildPopulationGroupRelevanceMap(results, group, measureScoringCode) {
    // Episode of care measure
    if (results.episodeResults && results.episodeResults.length > 0) {
        return buildPopulationRelevanceForAllEpisodes(results.episodeResults, group, measureScoringCode);
        // Normal patient based measure
    }
    else if (results.populationResults) {
        return buildPopulationRelevanceMap(results.populationResults, group, measureScoringCode);
    }
    else {
        // this shouldn't happen
        return [];
    }
}
exports.buildPopulationGroupRelevanceMap = buildPopulationGroupRelevanceMap;
/*
 * Find a matching result by populationType and (optionally) criteriaExpression
 * Using criteriaExpression and criteriaReferenceId is useful in cases where populationType is not specific enough
 *  (e.g. multiple IPPs or observations that use the same function)
 */
function findResult(populationType, results, criteriaExpression, criteriaReferenceId) {
    return results.find(result => {
        if (result.populationType === populationType) {
            return ((criteriaExpression ? result.criteriaExpression === criteriaExpression : true) &&
                (criteriaReferenceId ? result.criteriaReferenceId === criteriaReferenceId : true));
        }
        return false;
    });
}
exports.findResult = findResult;
function hasResult(populationType, results, criteriaExpression) {
    return findResult(populationType, results, criteriaExpression) != null;
}
exports.hasResult = hasResult;
// If the given population result is in the given result list, return the result
function getResult(populationType, results, criteriaExpression) {
    const popResult = findResult(populationType, results, criteriaExpression);
    return (popResult === null || popResult === void 0 ? void 0 : popResult.result) === true;
}
exports.getResult = getResult;
// If the given value is in the given populationSet, set the result to the new result
function setResult(populationType, newResult, results, criteriaExpression, criteriaReferenceId) {
    const popResult = findResult(populationType, results, criteriaExpression, criteriaReferenceId);
    if (popResult) {
        popResult.result = newResult;
    }
}
exports.setResult = setResult;
// create a result for the given population type and result or update the existing value to true if newResult is true
function createOrSetResult(populationType, newResult, results, criteriaExpression, populationId, criteriaReferenceId, observations) {
    const popResult = findResult(populationType, results, criteriaExpression, criteriaReferenceId);
    if (popResult) {
        if (newResult === true) {
            popResult.result = true;
        }
    }
    else {
        results.push(Object.assign(Object.assign(Object.assign({ populationType,
            criteriaExpression, result: newResult }, (populationId ? { populationId } : {})), (criteriaReferenceId ? { criteriaReferenceId } : {})), (observations && { observations })));
    }
}
exports.createOrSetResult = createOrSetResult;
// Get raw results of matching SDE expressions (both supplemental-data and risk-adjustment-factor usages) for each SDE in the Measure
function getSDEValues(measure, statementResults) {
    const results = [];
    if (measure.supplementalData) {
        measure.supplementalData.forEach(sde => {
            var _a, _b, _c, _d, _e;
            if ((_a = sde.criteria) === null || _a === void 0 ? void 0 : _a.expression) {
                const expression = sde.criteria.expression;
                const result = statementResults[expression];
                const usage = (_d = (_c = (_b = sde.usage) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.coding) === null || _d === void 0 ? void 0 : _d[0].code;
                if (!(0, Calculator_1.isSupplementalDataUsage)(usage)) {
                    throw new CustomErrors_1.UnexpectedProperty(`Received usage: ${usage}. Expected sde usage code from the MeasureDataUsage valueset: https://terminology.hl7.org/3.1.0/ValueSet-measure-data-usage.html`);
                }
                results.push({
                    name: ((_e = sde.code) === null || _e === void 0 ? void 0 : _e.text) || expression,
                    rawResult: result,
                    pretty: prettyResult(result),
                    id: sde.id,
                    criteriaExpression: expression,
                    usage: usage
                });
            }
        });
    }
    return results;
}
exports.getSDEValues = getSDEValues;
//# sourceMappingURL=ClauseResultsBuilder.js.map